import { useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Copy, Check, Link, FileText, FolderOpen } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  createShare,
  EXPIRY_OPTIONS,
  type CreateShareResult,
} from "@/stores/documents/repository/shares";
import { useLanguage } from "@/lib/i18n/LanguageContext";

interface ShareDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** IDs of selected documents to share */
  selectedDocumentIds: string[];
  /** IDs of selected folders to share */
  selectedFolderIds?: string[];
  /** Building ID */
  buildingId: string;
  /** Organization ID */
  orgId: string;
  /** Callback after successful share creation */
  onSuccess?: (result: CreateShareResult) => void;
}

/** Map EXPIRY_OPTIONS values to translation keys */
const EXPIRY_LABEL_KEYS: Record<
  number,
  "oneHour" | "twentyFourHours" | "sevenDays" | "thirtyDays"
> = {
  3600: "oneHour",
  86400: "twentyFourHours",
  604800: "sevenDays",
  2592000: "thirtyDays",
};

/**
 * Dialog for creating a share link for selected documents.
 * Allows setting expiry duration and optional title.
 */
export const ShareDialog = observer(function ShareDialog({
  open,
  onOpenChange,
  selectedDocumentIds,
  selectedFolderIds = [],
  buildingId,
  orgId,
  onSuccess,
}: ShareDialogProps) {
  const { toast } = useToast();
  const { t } = useLanguage();

  // Form state
  const [title, setTitle] = useState("");
  const [expirySeconds, setExpirySeconds] = useState(String(EXPIRY_OPTIONS[1].value)); // Default: 24 hours
  const [isCreating, setIsCreating] = useState(false);

  // Result state
  const [shareResult, setShareResult] = useState<CreateShareResult | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = useCallback(async () => {
    if (selectedDocumentIds.length === 0 && selectedFolderIds.length === 0) return;

    setIsCreating(true);

    try {
      const result = await createShare({
        documentIds: selectedDocumentIds,
        folderIds: selectedFolderIds,
        buildingId,
        orgId,
        expiresInSeconds: parseInt(expirySeconds, 10),
        title: title.trim() || undefined,
      });

      setShareResult(result);
      onSuccess?.(result);

      toast({
        title: t.documents.shareDialog.linkCreatedToast,
        description: t.documents.shareDialog.linkReadyToast,
      });
    } catch (error) {
      console.error("Error creating share:", error);
      toast({
        title: t.documents.shareDialog.failedCreate,
        description: error instanceof Error ? error.message : t.common.error,
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  }, [
    selectedDocumentIds,
    selectedFolderIds,
    buildingId,
    orgId,
    expirySeconds,
    title,
    onSuccess,
    toast,
    t,
  ]);

  const handleCopyLink = useCallback(async () => {
    if (!shareResult?.shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareResult.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      toast({
        title: t.documents.shareDialog.linkCopied,
        description: t.documents.shareDialog.linkCopiedToast,
      });
    } catch {
      toast({
        title: t.documents.shareDialog.failedCopy,
        description: t.documents.shareDialog.copyManually,
        variant: "destructive",
      });
    }
  }, [shareResult?.shareUrl, toast, t]);

  const handleClose = useCallback(() => {
    // Reset state when closing
    setTitle("");
    setExpirySeconds(String(EXPIRY_OPTIONS[1].value));
    setShareResult(null);
    setCopied(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const documentCount = selectedDocumentIds.length;
  const folderCount = selectedFolderIds.length;
  const totalCount = documentCount + folderCount;

  // Build description text
  const getDescriptionText = () => {
    const parts: string[] = [];
    if (documentCount > 0) {
      parts.push(
        `${documentCount} ${documentCount === 1 ? t.documents.shareDialog.document : t.documents.shareDialog.documents}`
      );
    }
    if (folderCount > 0) {
      parts.push(
        `${folderCount} ${folderCount === 1 ? t.documents.shareDialog.folder : t.documents.shareDialog.folders}`
      );
    }
    return parts.join(` ${t.documents.shareDialog.and} `);
  };

  /** Get translated label for an expiry option */
  const getExpiryLabel = (value: number): string => {
    const key = EXPIRY_LABEL_KEYS[value];
    return key ? t.documents.shareDialog.expiryOptions[key] : String(value);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            {t.documents.shareDialog.title}
          </DialogTitle>
          <DialogDescription>
            {t.documents.shareDialog.description.replace("{description}", getDescriptionText())}
          </DialogDescription>
        </DialogHeader>

        {!shareResult ? (
          // Creation form
          <div className="space-y-4 py-4">
            {/* Selection indicator */}
            <div className="flex flex-wrap items-center gap-3 rounded-md bg-muted p-3">
              {documentCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {documentCount}{" "}
                    {documentCount === 1
                      ? t.documents.shareDialog.document
                      : t.documents.shareDialog.documents}
                  </span>
                </div>
              )}
              {folderCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <FolderOpen className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {folderCount}{" "}
                    {folderCount === 1
                      ? t.documents.shareDialog.folder
                      : t.documents.shareDialog.folders}
                  </span>
                </div>
              )}
            </div>

            {/* Title input */}
            <div className="space-y-2">
              <Label htmlFor="share-title">{t.documents.shareDialog.titleLabel}</Label>
              <Input
                id="share-title"
                placeholder={t.documents.shareDialog.titlePlaceholder}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={isCreating}
              />
            </div>

            {/* Expiry select */}
            <div className="space-y-2">
              <Label htmlFor="share-expiry">{t.documents.shareDialog.expiryLabel}</Label>
              <Select value={expirySeconds} onValueChange={setExpirySeconds} disabled={isCreating}>
                <SelectTrigger id="share-expiry">
                  <SelectValue placeholder="Select expiry" />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {getExpiryLabel(option.value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          // Success state - show link
          <div className="space-y-4 py-4">
            <div className="rounded-md bg-muted p-3">
              <p className="mb-2 text-sm font-medium">{t.documents.shareDialog.linkCreated}</p>
              <div className="flex items-center gap-2">
                <Input readOnly value={shareResult.shareUrl} className="font-mono text-xs" />
                <Button variant="outline" size="icon" onClick={handleCopyLink} className="shrink-0">
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {t.documents.shareDialog.expiryNote.replace(
                "{expiry}",
                getExpiryLabel(parseInt(expirySeconds, 10))
              )}
            </p>
          </div>
        )}

        <DialogFooter>
          {!shareResult ? (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isCreating}>
                {t.common.cancel}
              </Button>
              <Button onClick={handleCreate} disabled={isCreating || totalCount === 0}>
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t.documents.shareDialog.creating}
                  </>
                ) : (
                  t.documents.shareDialog.createButton
                )}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose}>
                {t.common.done}
              </Button>
              <Button onClick={handleCopyLink}>
                {copied ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    {t.documents.shareDialog.copied}
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" />
                    {t.documents.shareDialog.copyLink}
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
